import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { ExpandMore, ExpandLess, TravelExplore } from '@mui/icons-material'
import { compatibilidadeParceiroService } from '@/services/produtoService'
import { ParceiroCompativel } from '@/types/produto'

/**
 * Parceiros compatíveis com a operação (motor de compatibilidade).
 *
 * **Filtro com motivo, não ranking** — a definição veio do time comercial: *"a plataforma
 * sugere e o operador direciona"*, apresentando *"para todos os parceiros elegíveis"*. Por
 * isso todos os parceiros aparecem, os incompatíveis marcados com o critério violado e os
 * dois valores lado a lado — o operador precisa poder discordar da máquina.
 *
 * Os campos extras são opcionais de propósito: **atributo não informado nunca elimina
 * parceiro**, vira pendência visível ("conferir à mão"). O operador refina quando souber
 * mais do cliente.
 */

interface Props {
  produtoId: string
  /** Pré-preenche o valor da operação com o estimado da reserva. */
  valorEstimado?: number | null
  /**
   * Ação de seleção formal (Handoff v4.0: manual, sem ranking). Presente só quando quem vê
   * PODE selecionar (ADMIN) — o card continua sendo sugestão; a decisão é este callback.
   */
  onSelecionar?: (parceiroId: string, nome: string) => Promise<void>
  /** Parceiro já escolhido, para o card marcar em vez de reoferecer. */
  parceiroSelecionadoId?: string | null
}

/** Características que o cliente PODE ter — marcar significa "tem"; em branco, "não sei". */
const CARACTERISTICAS = [
  { chave: 'mei', rotulo: 'MEI' },
  { chave: 'cliente_com_restricao', rotulo: 'Com restrição' },
  { chave: 'recuperacao_judicial', rotulo: 'Recuperação Judicial' },
  { chave: 'protestos', rotulo: 'Protestos' },
]

const ParceirosCompativeisCard: React.FC<Props> = ({
  produtoId, valorEstimado, onSelecionar, parceiroSelecionadoId,
}) => {
  const [parceiros, setParceiros] = useState<ParceiroCompativel[] | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [refinando, setRefinando] = useState(false)
  const [detalhe, setDetalhe] = useState<string | null>(null)
  const [selecionando, setSelecionando] = useState<string | null>(null)

  const [valor, setValor] = useState(valorEstimado ? String(valorEstimado) : '')
  const [prazo, setPrazo] = useState('')
  const [faturamento, setFaturamento] = useState('')
  const [segmento, setSegmento] = useState('')
  const [estado, setEstado] = useState('')
  const [caracteristicas, setCaracteristicas] = useState<Set<string>>(new Set())

  const avaliar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const atributos: Record<string, string> = {}
      if (valor.trim()) atributos.valor_operacao = valor.trim()
      if (prazo.trim()) atributos.prazo_meses = prazo.trim()
      if (faturamento.trim()) atributos.faturamento_anual = faturamento.trim()
      if (segmento.trim()) atributos.segmento = segmento.trim()
      if (estado.trim()) atributos.estado = estado.trim()
      // Só as marcadas viajam: em branco é "não sei", e "não sei" não elimina ninguém.
      caracteristicas.forEach((c) => {
        atributos[c] = 'true'
      })
      setParceiros(await compatibilidadeParceiroService.avaliar(produtoId, atributos))
    } catch {
      setErro('Não foi possível avaliar os parceiros.')
    } finally {
      setCarregando(false)
    }
  }, [produtoId, valor, prazo, faturamento, segmento, estado, caracteristicas])

  useEffect(() => {
    void avaliar()
    // Reavalia só no clique de "Reavaliar" — reavaliar a cada tecla faria uma chamada por
    // dígito digitado no valor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produtoId])

  const compativeis = parceiros?.filter((p) => p.compativel).length ?? 0

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Parceiros compatíveis"
        subheader={
          parceiros ? `${compativeis} de ${parceiros.length} servem para esta operação` : undefined
        }
        action={
          <Button
            size="small"
            startIcon={refinando ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setRefinando((r) => !r)}
          >
            Refinar
          </Button>
        }
      />
      <CardContent>
        <Collapse in={refinando}>
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1.5}>
              <TextField label="Valor da operação" size="small" type="number" fullWidth
                value={valor} onChange={(e) => setValor(e.target.value)} />
              <TextField label="Prazo (meses)" size="small" type="number" fullWidth
                value={prazo} onChange={(e) => setPrazo(e.target.value)} />
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <TextField label="Faturamento anual" size="small" type="number" fullWidth
                value={faturamento} onChange={(e) => setFaturamento(e.target.value)} />
              <TextField label="Estado (UF)" size="small" fullWidth
                value={estado} onChange={(e) => setEstado(e.target.value)} />
            </Stack>
            <TextField label="Segmento" size="small" fullWidth
              value={segmento} onChange={(e) => setSegmento(e.target.value)} />
            <Box>
              <Typography variant="caption" color="textSecondary">
                Marque as características que o cliente possui — em branco significa “não
                sei”, e não elimina parceiro.
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap>
                {CARACTERISTICAS.map((c) => (
                  <FormControlLabel
                    key={c.chave}
                    control={
                      <Checkbox
                        size="small"
                        checked={caracteristicas.has(c.chave)}
                        onChange={(e) =>
                          setCaracteristicas((prev) => {
                            const novo = new Set(prev)
                            if (e.target.checked) novo.add(c.chave)
                            else novo.delete(c.chave)
                            return novo
                          })
                        }
                      />
                    }
                    label={<Typography variant="body2">{c.rotulo}</Typography>}
                  />
                ))}
              </Stack>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<TravelExplore />}
              onClick={() => void avaliar()}
              disabled={carregando}
            >
              Reavaliar
            </Button>
            <Divider />
          </Stack>
        </Collapse>

        {carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} aria-label="Avaliando parceiros" />
          </Box>
        ) : erro ? (
          <Alert
            severity="error"
            action={
              <Button size="small" color="inherit" onClick={() => void avaliar()}>
                Tentar de novo
              </Button>
            }
          >
            {erro}
          </Alert>
        ) : !parceiros || parceiros.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            Nenhum parceiro vinculado a este produto.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {parceiros.map((p) => (
              <Box key={p.parceiroId}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {p.nome}
                    </Typography>
                    {p.observacoesComerciais && (
                      <Typography variant="caption" color="textSecondary">
                        {p.observacoesComerciais}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {parceiroSelecionadoId === p.parceiroId && (
                      <Chip size="small" label="Selecionado" color="primary" />
                    )}
                    {onSelecionar && parceiroSelecionadoId !== p.parceiroId && (
                      // A seleção não exige compatibilidade: o filtro sugere, o ADMIN
                      // decide — inclusive discordando da máquina (é sugestão, não trava).
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={selecionando !== null}
                        onClick={() => {
                          setSelecionando(p.parceiroId)
                          void onSelecionar(p.parceiroId, p.nome).finally(() => setSelecionando(null))
                        }}
                      >
                        {selecionando === p.parceiroId ? 'Selecionando…' : 'Selecionar'}
                      </Button>
                    )}
                    {p.semPolitica ? (
                      <Tooltip title="Opera o produto, mas a política de elegibilidade ainda não foi cadastrada — compatível por ausência de restrição, não por análise.">
                        <Chip size="small" label="Sem política" color="warning" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        label={p.compativel ? 'Compatível' : 'Não compatível'}
                        color={p.compativel ? 'success' : 'default'}
                        variant={p.compativel ? 'filled' : 'outlined'}
                      />
                    )}
                    {(p.motivos.length > 0 || p.naoAvaliados.length > 0 || p.criteriosEliminatorios) && (
                      <Button
                        size="small"
                        onClick={() =>
                          setDetalhe((d) => (d === p.parceiroId ? null : p.parceiroId))
                        }
                      >
                        {detalhe === p.parceiroId ? 'Ocultar' : 'Detalhes'}
                      </Button>
                    )}
                  </Stack>
                </Stack>

                <Collapse in={detalhe === p.parceiroId}>
                  <Box sx={{ pl: 1, pt: 1 }}>
                    {p.motivos.map((m, i) => (
                      <Typography key={i} variant="caption" color="error" display="block">
                        • {m.rotulo}: exigido {m.valorPolitica}
                        {m.valorOperacao ? `, informado ${m.valorOperacao}` : ''}
                      </Typography>
                    ))}
                    {p.naoAvaliados.length > 0 && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        Conferir à mão: {p.naoAvaliados.join(' · ')}
                      </Typography>
                    )}
                    {p.criteriosEliminatorios && (
                      <Alert severity="warning" sx={{ mt: 1 }} icon={false}>
                        <Typography variant="caption">
                          <strong>Critérios eliminatórios do parceiro:</strong>{' '}
                          {p.criteriosEliminatorios}
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}

export default ParceirosCompativeisCard
